import type { ParsedFlowDefinition } from "./normalize.js";

function semanticHashInput(definition: ParsedFlowDefinition): ParsedFlowDefinition {
  const defaults = definition.defaults ? { ...definition.defaults } : undefined;
  if (defaults?.parameters && Object.keys(defaults.parameters).length === 0) {
    delete defaults.parameters;
  }

  const meta = definition.meta ? { ...definition.meta } : undefined;
  if (meta?.tags?.length === 0) delete meta.tags;

  const normalized: ParsedFlowDefinition = {
    ...definition,
    nodes: definition.nodes.map((node) => {
      const run: Record<string, unknown> = { ...node.run };
      if (node.run.assets) {
        const assets = [...new Set(node.run.assets)].sort();
        if (assets.length > 0) run.assets = assets;
        else delete run.assets;
      }
      if (
        "capture" in node.run &&
        node.run.capture !== undefined &&
        Object.keys(node.run.capture).length === 0
      ) {
        delete run.capture;
      }
      // "default" inherits rather than naming a model, so it is the no-op value for
      // this field on the kinds where that semantics is defined — strip it like the
      // other default-valued control fields below. Kind-scoped so a future run kind
      // whose "default" is not a no-op is never silently stripped from identity.
      if (
        (node.run.kind === "agent" || node.run.kind === "subprompt") &&
        "model" in node.run &&
        node.run.model === "default"
      ) {
        delete run.model;
      }

      const control = node.control ? { ...node.control } : undefined;
      if (control?.optional === false) delete control.optional;
      if (control?.retries === 0) delete control.retries;

      const contract = node.contract ? { ...node.contract } : undefined;
      if (contract?.requires?.length === 0) delete contract.requires;
      if (contract?.input && Object.keys(contract.input).length === 0) delete contract.input;
      if (contract?.output) {
        const output = { ...contract.output };
        if (output.artifacts?.length === 0) delete output.artifacts;
        contract.output = Object.keys(output).length > 0 ? output : undefined;
      }

      const normalizedNode: Record<string, unknown> = { ...node, run };
      if (control && Object.keys(control).length > 0) normalizedNode.control = control;
      else delete normalizedNode.control;
      if (contract && Object.values(contract).some((value) => value !== undefined)) {
        normalizedNode.contract = contract;
      } else {
        delete normalizedNode.contract;
      }
      if (node.gate?.approval === "none") delete normalizedNode.gate;
      return normalizedNode as ParsedFlowDefinition["nodes"][number];
    }),
  };
  normalized.defaults = defaults && Object.keys(defaults).length > 0 ? defaults : undefined;
  normalized.meta = meta && Object.keys(meta).length > 0 ? meta : undefined;
  return normalized;
}

function canonicalize(value: unknown): string | undefined {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalize(item) ?? "null").join(",")}]`;
  }
  const object = value as Record<string, unknown>;
  const entries: string[] = [];
  for (const key of Object.keys(object).sort()) {
    const serialized = canonicalize(object[key]);
    if (serialized !== undefined) entries.push(`${JSON.stringify(key)}:${serialized}`);
  }
  return `{${entries.join(",")}}`;
}

/** Gives equivalent definitions identical bytes while preserving content-significant array order. */
export function canonicalizeFlowDefinition(definition: ParsedFlowDefinition): string {
  return canonicalize(semanticHashInput(definition)) ?? "null";
}

/** Creates a browser-safe content identity for caching, pinning, and change detection. */
export async function hashFlowDefinition(definition: ParsedFlowDefinition): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalizeFlowDefinition(definition));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
